// ink-component
import React, { ReactNode, RefObject, FocusEventHandler } from 'react';
import { ThemeProvider } from 'styled-components';

import Field from '../Field/Field';
import NewCheckbox from '../NewCheckbox/NewCheckbox';

export interface AgreementProps {
  /** Unique identifier */
  id: string;
  /** Unique name */
  name?: string;
  /** Label of the field */
  label?: ReactNode;
  /** onChange callback. Passes the Event */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Error contents */
  error?: ReactNode;
  /** Help contents */
  help?: ReactNode;
  /** Info contents */
  info?: ReactNode;
  /** External control and initial value for the checkbox */
  value?: boolean;
  /** Anchor linking to the associated document */
  attachment?: ReactNode;
  /** External control and initial value for the checkbox */
  isChecked?: boolean;
  /** Modifier for disabled state */
  isDisabled?: boolean;
  /** HTML type */
  autoFocus?: boolean;
  /** onBlur callback */
  onBlur?: FocusEventHandler<HTMLInputElement>;
  /** React ref object to be forwarded to the internal node */
  externalRef?: RefObject<HTMLInputElement>;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Unique name for analytics purposes. By default, components are not tracked, unless this is set */
  'data-track-name'?: string;
  /** Custom payload to be sent to analytics provider */
  'data-track-payload'?: any;
  /** List of tracked events in the analytics integration */
  'data-track-events'?: string[];
}

/**
 * @purpose Allows users to agree with statements and attachments
 * @categories form
 * @aka terms and conditions, t&c
 */
function Agreement(props: AgreementProps) {
  const { label, error, 'data-testid': dataTestId, isChecked, isDisabled, value, onBlur, ...otherProps } = props;

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        fieldIsAgreement: true,
      })}
    >
      <Field {...otherProps} error={error} isStacked isFull isRequired density="high" data-testid={dataTestId}>
        <NewCheckbox
          {...otherProps}
          checked={isChecked || value}
          disabled={isDisabled}
          width="full"
          label={label}
          invalid={Boolean(error)}
          onBlur={onBlur}
        />
      </Field>
    </ThemeProvider>
  );
}

Agreement.defaultProps = {
  name: undefined,
  label: undefined,
  onChange: undefined,
  error: undefined,
  help: undefined,
  info: undefined,
  attachment: undefined,
  value: undefined,
  isChecked: undefined,
  isDisabled: false,
  autoFocus: false,
  externalRef: undefined,
  'data-testid': undefined,
  'data-track-name': undefined,
  'data-track-payload': undefined,
  'data-track-events': undefined,
};

Agreement.displayName = 'Agreement';

export default Agreement;
